// Sidebar.js
import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Flex,
  Drawer,
  DrawerBody,
  Icon,
  useColorModeValue,
  DrawerOverlay,
  useDisclosure,
  DrawerContent,
  DrawerCloseButton,
  Button,
} from "@chakra-ui/react";
import Content from "components/sidebar/components/Content";
import { Scrollbars } from "react-custom-scrollbars-2";
import { IoMenuOutline, IoLogOutOutline } from "react-icons/io5";
import { renderThumb, renderTrack, renderView } from "components/scrollbar/Scrollbar";
import { useAuth } from "../../contexts/AuthContext"; // Auth context
import { useNavigate } from "react-router-dom";

function Sidebar({ routes }) {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    const res = await logout();
    if (res.success) {
      navigate("/auth/sign-in");
    }
  };

  const variantChange = "0.2s linear";
  const shadow = useColorModeValue(
    "14px 17px 40px 4px rgba(112, 144, 176, 0.08)",
    "unset"
  );
  const sidebarBg = useColorModeValue("white", "navy.800");
  const filteredRoutes = routes.filter((route) => route.layout !== "/auth");

  return (
    <Box display={{ sm: "none", xl: "block" }} w="100%" position="fixed" minH="100%">
      <Box
        bg={sidebarBg}
        transition={variantChange}
        w="300px"
        h="100vh"
        minH="100%"
        overflowX="hidden"
        boxShadow={shadow}
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
      >
        {/* Scrollable menu items */}
        <Box flex="1" overflowY="auto">
          <Scrollbars
            autoHide
            renderTrackVertical={renderTrack}
            renderThumbVertical={renderThumb}
            renderView={renderView}
          >
            <Content routes={filteredRoutes} />
          </Scrollbars>
        </Box>

        {/* Logout button fixed at bottom */}
        <Box p="10px">
          <Button
            w="100%"
            colorScheme="orange"
            leftIcon={<IoLogOutOutline />}
            onClick={handleLogout}
          >
            Logout
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

// Responsive Sidebar (Drawer)
export function SidebarResponsive({ routes }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = React.useRef();
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    const res = await logout();
    if (res.success) {
      onClose();
      navigate("/auth/sign-in");
    }
  };

  const sidebarBg = useColorModeValue("white", "navy.800");
  const menuColor = useColorModeValue("gray.400", "white");

  // Filter out auth routes
  const filteredRoutes = routes.filter((route) => route.layout !== "/auth");

  return (
    <Flex display={{ sm: "flex", xl: "none" }} alignItems="center">
      <Flex ref={btnRef} w="max-content" h="max-content" onClick={onOpen}>
        <Icon
          as={IoMenuOutline}
          color={menuColor}
          my="auto"
          w="20px"
          h="20px"
          me="10px"
          _hover={{ cursor: "pointer" }}
        />
      </Flex>

      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        placement={document.documentElement.dir === "rtl" ? "right" : "left"}
        finalFocusRef={btnRef}
      >
        <DrawerOverlay />
        <DrawerContent w="285px" maxW="285px" bg={sidebarBg}>
          <DrawerCloseButton
            zIndex="3"
            onClose={onClose}
            _focus={{ boxShadow: "none" }}
            _hover={{ boxShadow: "none" }}
          />

          <DrawerBody p="0" display="flex" flexDirection="column" justifyContent="space-between" h="100%">
            <Box flex="1" overflowY="auto">
              <Scrollbars
                autoHide
                renderTrackVertical={renderTrack}
                renderThumbVertical={renderThumb}
                renderView={renderView}
              >
                <Content routes={filteredRoutes} />
              </Scrollbars>
            </Box>

            <Box p="10px">
              <Button
                w="100%"
                colorScheme="red"
                leftIcon={<IoLogOutOutline />}
                onClick={handleLogout}
              >
                Logout
              </Button>
            </Box>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Flex>
  );
}

Sidebar.propTypes = {
  logoText: PropTypes.string,
  routes: PropTypes.arrayOf(PropTypes.object),
  variant: PropTypes.string,
};

export default Sidebar;
